import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  parseAsBoolean,
  parseAsInteger,
  parseAsString,
  useQueryStates,
} from 'nuqs';
import type {
  OpenOasisSortBy,
  OpenOasisSortDirection,
} from '../../services/api/types.gen';
import { formatDateToISO } from '../../utils/dateUtils';
import { useDebounce } from '../useDebounce';
import {
  createReportSortParser,
  parseAsReportDate,
  parseAsReportSortDirection,
} from './reportQueryParsers';

const SORT_KEYS = [
  'clinic_name',
  'patient',
  'mrn',
  'visit_type',
  'target_date',
  'days_outstanding',
  'status',
  'assigned_clinician',
  'episode_payor',
  'payor_type',
  'notes',
] as const;
const parsers = {
  clinic: parseAsString.withDefault(''),
  q: parseAsString.withDefault(''),
  status: parseAsString.withDefault(''),
  visit_type: parseAsString.withDefault(''),
  clinician: parseAsString.withDefault(''),
  unassigned: parseAsBoolean.withDefault(false),
  payor_type: parseAsString.withDefault(''),
  include_test_rows: parseAsBoolean.withDefault(false),
  include_inactive_episodes: parseAsBoolean.withDefault(false),
  hideImportedPatients: parseAsBoolean.withDefault(true),
  days_min: parseAsInteger,
  days_max: parseAsInteger,
  from: parseAsReportDate,
  to: parseAsReportDate,
  sort: createReportSortParser(SORT_KEYS).withDefault('days_outstanding'),
  dir: parseAsReportSortDirection.withDefault('desc'),
  page: parseAsInteger.withDefault(1),
  size: parseAsInteger.withDefault(25),
};
type OpenOasisUrlState = {
  clinic: string;
  q: string;
  status: string;
  visit_type: string;
  clinician: string;
  unassigned: boolean;
  payor_type: string;
  include_test_rows: boolean;
  include_inactive_episodes: boolean;
  hideImportedPatients: boolean;
  days_min: number | null;
  days_max: number | null;
  from: Date | null;
  to: Date | null;
  sort: (typeof SORT_KEYS)[number];
  dir: 'asc' | 'desc';
  page: number;
  size: number;
};
type Filters = Partial<OpenOasisUrlState>;
const date = (value: Date | null | undefined) =>
  value ? formatDateToISO(value) : undefined;
const ids = (value: string) =>
  value
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);

export function useOpenOasisReportParams() {
  const [url, setUrl] = useQueryStates(parsers, {
    history: 'replace',
    clearOnDefault: true,
  });
  const [searchInput, setSearchInputState] = useState(url.q);
  const update = useCallback(
    (updates: Filters, reset = true) =>
      setUrl({ ...updates, ...(reset ? { page: 1 } : {}) }),
    [setUrl]
  );
  const setSearchQuery = useCallback(
    (value: string) => update({ q: value.trim() }),
    [update]
  );
  const settleSearch = useCallback(
    (value: string) => {
      setSearchQuery(value);
      setSearchInputState(currentValue =>
        currentValue === value ? value.trim() : currentValue
      );
    },
    [setSearchQuery]
  );
  const debouncedSearch = useDebounce(settleSearch, 300);
  const setSearchInput = useCallback(
    (value: string) => {
      setSearchInputState(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );
  useEffect(() => {
    debouncedSearch.cancel();
    setSearchInputState(url.q);
  }, [debouncedSearch, url.q]);
  const tableQuery = useMemo(
    () => ({
      clinic_id: url.clinic || undefined,
      q: url.q || undefined,
      statuses: url.status || undefined,
      assigned_clinician_ids: ids(url.clinician),
      unassigned: url.unassigned || undefined,
      visit_types: url.visit_type || undefined,
      payor_types: url.payor_type || undefined,
      include_test_rows: url.include_test_rows,
      include_inactive_episodes: url.include_inactive_episodes,
      days_outstanding_min: url.days_min ?? undefined,
      days_outstanding_max: url.days_max ?? undefined,
      target_date_from: date(url.from),
      target_date_to: date(url.to),
      sort_by: url.sort,
      sort_direction: url.dir,
      page: url.page,
      page_size: url.size,
    }),
    [url]
  );
  return {
    params: url,
    searchInput,
    setSearchInput,
    setSearchQuery: debouncedSearch,
    isSearchPending: searchInput !== url.q,
    setFilters: (filters: Filters, resetPage = true) =>
      update(filters, resetPage),
    setSorting: (sort: OpenOasisSortBy, dir: OpenOasisSortDirection) =>
      update({ sort, dir }),
    setPagination: (page: number, size: number) =>
      update({ page, size }, false),
    tableQuery,
    exportQuery: tableQuery,
  };
}
